import React, { useState, useRef } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

export default function ProfileImageUpload({ currentImage, name, size = 'lg', onUploadSuccess }) {
  const { token } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(null);
  const fileInputRef = useRef(null);

  const sizeClasses = {
    sm: 'w-12 h-12 text-lg',
    md: 'w-16 h-16 text-xl',
    lg: 'w-24 h-24 text-3xl',
    xl: 'w-32 h-32 text-4xl'
  };

  // Build image display URL
  const displayUrl = imageUrl || (currentImage ? `${API}/files/${currentImage}` : null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!['image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(file.type)) {
      toast.error('Only JPEG, PNG, WebP, or GIF images allowed');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/upload/profile-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      // Set local blob URL for immediate display
      setImageUrl(URL.createObjectURL(file));
      toast.success('Profile image updated!');
      if (onUploadSuccess) onUploadSuccess(res.data.path);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="relative group" data-testid="profile-image-upload">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        onChange={handleUpload}
        className="hidden"
        data-testid="profile-image-input"
      />
      <div
        className={`${sizeClasses[size]} rounded-xl flex items-center justify-center font-bold overflow-hidden cursor-pointer transition-all ${
          displayUrl ? '' : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
        }`}
        onClick={() => fileInputRef.current?.click()}
      >
        {uploading ? (
          <div className="flex items-center justify-center w-full h-full bg-slate-200">
            <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : displayUrl ? (
          <img
            src={displayUrl}
            alt={name || 'Profile'}
            className="w-full h-full object-cover"
            data-testid="profile-image-display"
          />
        ) : (
          name?.charAt(0)?.toUpperCase() || '?'
        )}
      </div>
      {/* Camera overlay on hover */}
      <div
        className="absolute inset-0 bg-black/40 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
        onClick={() => fileInputRef.current?.click()}
      >
        <Camera className="w-6 h-6 text-white" />
      </div>
    </div>
  );
}

// Read-only display version (for profile pages)
export function ProfileImage({ imagePath, name, size = 'lg', className = '' }) {
  const sizeClasses = {
    sm: 'w-10 h-10 text-sm',
    md: 'w-16 h-16 text-xl',
    lg: 'w-24 h-24 text-3xl',
    xl: 'w-32 h-32 text-4xl'
  };

  const displayUrl = imagePath ? `${API}/files/${imagePath}` : null;

  return (
    <div
      className={`${sizeClasses[size]} rounded-xl flex items-center justify-center font-bold overflow-hidden ${
        displayUrl ? '' : 'bg-gradient-to-br from-blue-500 to-blue-600 text-white'
      } ${className}`}
      data-testid="profile-image"
    >
      {displayUrl ? (
        <img
          src={displayUrl}
          alt={name || 'Profile'}
          className="w-full h-full object-cover"
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.parentNode.classList.add('bg-gradient-to-br', 'from-blue-500', 'to-blue-600', 'text-white');
            e.target.parentNode.textContent = name?.charAt(0)?.toUpperCase() || '?';
          }}
        />
      ) : (
        name?.charAt(0)?.toUpperCase() || '?'
      )}
    </div>
  );
}
